import { gql, type TypedDocumentNode } from "@apollo/client";
import type {
  IEarningRule,
  IEarningRuleInput,
  IEntityReference,
  IPointAdjustmentInput,
  IRedemption,
  IRewardAuditEntry,
  IRewardCatalogItem,
  IRewardCatalogItemInput,
  IRewardMember,
  IRewardMemberDetail,
  IRewardProgram,
  IRewardProgramInput,
  IRewardTier,
  IRewardTierInput,
  IRewardTransaction,
  IRewardsDashboard,
  IRuleSimulation,
  IRuleSimulationContext,
} from "@ticketon/rewards-core";
import {
  CATALOG_ITEM_FIELDS,
  EARNING_RULE_FIELDS,
  MONEY_FIELDS,
  PAGE_FIELDS,
  PROGRAM_FIELDS,
  REDEMPTION_FIELDS,
  REWARD_BALANCE_FIELDS,
  TIER_FIELDS,
  TRANSACTION_FIELDS,
} from "./fragments";
import type { IConnection, IPageVariables } from "../pagination";

/*
 * Admin operations are served by the separate `/platform/admin/rewards/graphql`
 * endpoint (Ticketon.Rewards README §4). Use `RewardsApolloProvider` to point
 * the hooks at a client for that endpoint.
 */

/** `RewardMember` selection shared by member search and member detail. */
const MEMBER_FIELDS = gql`
  fragment RewardMemberFields on RewardMember {
    id
    memberType
    name
    email
    phone
    externalId
    programId
    segment
    enrolledAt
  }
`;

/* ----------------------------- Dashboard ------------------------------ */

export const GET_REWARDS_DASHBOARD: TypedDocumentNode<
  { rewardsDashboard: IRewardsDashboard },
  { from?: string; to?: string }
> = gql`
  query GetRewardsDashboard($from: String, $to: String) {
    rewardsDashboard(from: $from, to: $to) {
      totalActiveMembers
      activeCustomerMembers
      activePromoterMembers
      pointsIssued
      pointsRedeemed
      outstandingPointLiability {
        ${MONEY_FIELDS}
      }
      expiredPoints
      redemptionRate
      averageBalance
      activePrograms
      activeEarningRules
      activeCampaigns
      membersByTier {
        label
        value
      }
      pointsIssuedBySource {
        label
        value
      }
      pointsIssuedByPurchaseType {
        label
        value
      }
      pointsIssuedByEvent {
        label
        value
      }
      topPromoterEarners {
        memberId
        name
        points
        tierName
      }
      topCustomerEarners {
        memberId
        name
        points
        tierName
      }
    }
  }
`;

/* ------------------------------ Programs ------------------------------ */

export interface IProgramFilterInput {
  search?: string;
  memberType?: string;
  status?: string;
}

export const GET_REWARD_PROGRAMS: TypedDocumentNode<
  { rewardPrograms: IConnection<IRewardProgram> },
  IPageVariables & { filter?: IProgramFilterInput }
> = gql`
  ${PROGRAM_FIELDS}
  query GetRewardPrograms($pageNumber: Int!, $pageSize: Int!, $filter: RewardProgramFilterInput) {
    rewardPrograms(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        ...RewardProgramFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_REWARD_PROGRAM: TypedDocumentNode<
  { rewardProgram: IRewardProgram | null },
  { id: string }
> = gql`
  ${PROGRAM_FIELDS}
  query GetRewardProgram($id: ID!) {
    rewardProgram(id: $id) {
      ...RewardProgramFields
    }
  }
`;

export const CREATE_REWARD_PROGRAM: TypedDocumentNode<
  { createRewardProgram: IRewardProgram },
  { input: IRewardProgramInput }
> = gql`
  ${PROGRAM_FIELDS}
  mutation CreateRewardProgram($input: RewardProgramInput!) {
    createRewardProgram(input: $input) {
      ...RewardProgramFields
    }
  }
`;

export const UPDATE_REWARD_PROGRAM: TypedDocumentNode<
  { updateRewardProgram: IRewardProgram },
  { id: string; input: IRewardProgramInput }
> = gql`
  ${PROGRAM_FIELDS}
  mutation UpdateRewardProgram($id: ID!, $input: RewardProgramInput!) {
    updateRewardProgram(id: $id, input: $input) {
      ...RewardProgramFields
    }
  }
`;

export const CHANGE_REWARD_PROGRAM_STATUS: TypedDocumentNode<
  { changeRewardProgramStatus: IRewardProgram },
  { id: string; status: string }
> = gql`
  ${PROGRAM_FIELDS}
  mutation ChangeRewardProgramStatus($id: ID!, $status: ProgramStatus!) {
    changeRewardProgramStatus(id: $id, status: $status) {
      ...RewardProgramFields
    }
  }
`;

export const DUPLICATE_REWARD_PROGRAM: TypedDocumentNode<
  { duplicateRewardProgram: IRewardProgram },
  { id: string }
> = gql`
  ${PROGRAM_FIELDS}
  mutation DuplicateRewardProgram($id: ID!) {
    duplicateRewardProgram(id: $id) {
      ...RewardProgramFields
    }
  }
`;

/* -------------------------------- Tiers ------------------------------- */

export const GET_REWARD_TIERS: TypedDocumentNode<
  { rewardTiers: IRewardTier[] },
  { programId: string }
> = gql`
  ${TIER_FIELDS}
  query GetRewardTiers($programId: ID!) {
    rewardTiers(programId: $programId) {
      ...RewardTierFields
    }
  }
`;

export const GET_REWARD_TIER: TypedDocumentNode<
  { rewardTier: IRewardTier | null },
  { id: string }
> = gql`
  ${TIER_FIELDS}
  query GetRewardTier($id: ID!) {
    rewardTier(id: $id) {
      ...RewardTierFields
    }
  }
`;

export const CREATE_REWARD_TIER: TypedDocumentNode<
  { createRewardTier: IRewardTier },
  { input: IRewardTierInput }
> = gql`
  ${TIER_FIELDS}
  mutation CreateRewardTier($input: RewardTierInput!) {
    createRewardTier(input: $input) {
      ...RewardTierFields
    }
  }
`;

export const UPDATE_REWARD_TIER: TypedDocumentNode<
  { updateRewardTier: IRewardTier },
  { id: string; input: IRewardTierInput }
> = gql`
  ${TIER_FIELDS}
  mutation UpdateRewardTier($id: ID!, $input: RewardTierInput!) {
    updateRewardTier(id: $id, input: $input) {
      ...RewardTierFields
    }
  }
`;

export const DELETE_REWARD_TIER: TypedDocumentNode<
  { deleteRewardTier: { id: string } },
  { id: string }
> = gql`
  mutation DeleteRewardTier($id: ID!) {
    deleteRewardTier(id: $id) {
      id
    }
  }
`;

export const REORDER_REWARD_TIERS: TypedDocumentNode<
  { reorderRewardTiers: IRewardTier[] },
  { programId: string; orderedTierIds: string[] }
> = gql`
  ${TIER_FIELDS}
  mutation ReorderRewardTiers($programId: ID!, $orderedTierIds: [ID!]!) {
    reorderRewardTiers(programId: $programId, orderedTierIds: $orderedTierIds) {
      ...RewardTierFields
    }
  }
`;

/* -------------------------------- Rules ------------------------------- */

export interface IRuleFilterInput {
  search?: string;
  programId?: string;
  memberType?: string;
  status?: string;
}

export const GET_REWARD_RULES: TypedDocumentNode<
  { rewardRules: IConnection<IEarningRule> },
  IPageVariables & { filter?: IRuleFilterInput }
> = gql`
  ${EARNING_RULE_FIELDS}
  query GetRewardRules($pageNumber: Int!, $pageSize: Int!, $filter: RewardRuleFilterInput) {
    rewardRules(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        ...EarningRuleFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_REWARD_RULE: TypedDocumentNode<
  { rewardRule: IEarningRule | null },
  { id: string }
> = gql`
  ${EARNING_RULE_FIELDS}
  query GetRewardRule($id: ID!) {
    rewardRule(id: $id) {
      ...EarningRuleFields
    }
  }
`;

export const CREATE_REWARD_RULE: TypedDocumentNode<
  { createRewardRule: IEarningRule },
  { input: IEarningRuleInput }
> = gql`
  ${EARNING_RULE_FIELDS}
  mutation CreateRewardRule($input: EarningRuleInput!) {
    createRewardRule(input: $input) {
      ...EarningRuleFields
    }
  }
`;

export const UPDATE_REWARD_RULE: TypedDocumentNode<
  { updateRewardRule: IEarningRule },
  { id: string; input: IEarningRuleInput }
> = gql`
  ${EARNING_RULE_FIELDS}
  mutation UpdateRewardRule($id: ID!, $input: EarningRuleInput!) {
    updateRewardRule(id: $id, input: $input) {
      ...EarningRuleFields
    }
  }
`;

export const CHANGE_REWARD_RULE_STATUS: TypedDocumentNode<
  { changeRewardRuleStatus: IEarningRule },
  { id: string; status: string }
> = gql`
  ${EARNING_RULE_FIELDS}
  mutation ChangeRewardRuleStatus($id: ID!, $status: RuleStatus!) {
    changeRewardRuleStatus(id: $id, status: $status) {
      ...EarningRuleFields
    }
  }
`;

export const DUPLICATE_REWARD_RULE: TypedDocumentNode<
  { duplicateRewardRule: IEarningRule },
  { id: string }
> = gql`
  ${EARNING_RULE_FIELDS}
  mutation DuplicateRewardRule($id: ID!) {
    duplicateRewardRule(id: $id) {
      ...EarningRuleFields
    }
  }
`;

export const SIMULATE_REWARD_RULE: TypedDocumentNode<
  { simulateRewardRule: IRuleSimulation },
  { context: IRuleSimulationContext; programId: string }
> = gql`
  mutation SimulateRewardRule($programId: ID!, $context: RuleSimulationContextInput!) {
    simulateRewardRule(programId: $programId, context: $context) {
      totalPoints
      results {
        ruleId
        ruleName
        matched
        rawPoints
        appliedPoints
        limitsApplied
        stackable
        reasons {
          description
          passed
        }
      }
    }
  }
`;

/* ------------------------------ Catalog ------------------------------- */

export interface ICatalogAdminFilterInput {
  search?: string;
  status?: string;
  rewardType?: string;
  programId?: string;
}

export const GET_REWARD_CATALOG_ITEMS: TypedDocumentNode<
  { rewardCatalogItems: IConnection<IRewardCatalogItem> },
  IPageVariables & { filter?: ICatalogAdminFilterInput }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  query GetRewardCatalogItems($pageNumber: Int!, $pageSize: Int!, $filter: RewardCatalogAdminFilterInput) {
    rewardCatalogItems(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        ...RewardCatalogItemFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_REWARD_CATALOG_ITEM: TypedDocumentNode<
  { rewardCatalogItem: IRewardCatalogItem | null },
  { id: string }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  query GetRewardCatalogItem($id: ID!) {
    rewardCatalogItem(id: $id) {
      ...RewardCatalogItemFields
    }
  }
`;

export const CREATE_REWARD_CATALOG_ITEM: TypedDocumentNode<
  { createRewardCatalogItem: IRewardCatalogItem },
  { input: IRewardCatalogItemInput }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  mutation CreateRewardCatalogItem($input: RewardCatalogItemInput!) {
    createRewardCatalogItem(input: $input) {
      ...RewardCatalogItemFields
    }
  }
`;

export const UPDATE_REWARD_CATALOG_ITEM: TypedDocumentNode<
  { updateRewardCatalogItem: IRewardCatalogItem },
  { id: string; input: IRewardCatalogItemInput }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  mutation UpdateRewardCatalogItem($id: ID!, $input: RewardCatalogItemInput!) {
    updateRewardCatalogItem(id: $id, input: $input) {
      ...RewardCatalogItemFields
    }
  }
`;

export const CHANGE_REWARD_CATALOG_ITEM_STATUS: TypedDocumentNode<
  { changeRewardCatalogItemStatus: IRewardCatalogItem },
  { id: string; status: string }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  mutation ChangeRewardCatalogItemStatus($id: ID!, $status: CatalogItemStatus!) {
    changeRewardCatalogItemStatus(id: $id, status: $status) {
      ...RewardCatalogItemFields
    }
  }
`;

/* ------------------------------ Members ------------------------------- */

export interface IMemberSearchInput {
  query: string;
  memberType?: string;
}

export const SEARCH_REWARD_MEMBERS: TypedDocumentNode<
  { searchRewardMembers: IConnection<IRewardMember> },
  IPageVariables & { input: IMemberSearchInput }
> = gql`
  ${MEMBER_FIELDS}
  query SearchRewardMembers($pageNumber: Int!, $pageSize: Int!, $input: MemberSearchInput!) {
    searchRewardMembers(pageNumber: $pageNumber, pageSize: $pageSize, input: $input) {
      items {
        ...RewardMemberFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_REWARD_MEMBER: TypedDocumentNode<
  { rewardMember: IRewardMemberDetail | null },
  { id: string }
> = gql`
  ${REWARD_BALANCE_FIELDS}
  ${MEMBER_FIELDS}
  query GetRewardMember($id: ID!) {
    rewardMember(id: $id) {
      member {
        ...RewardMemberFields
      }
      balance {
        ...RewardBalanceFields
      }
    }
  }
`;

export const GET_REWARD_MEMBER_TRANSACTIONS: TypedDocumentNode<
  { rewardMemberTransactions: IConnection<IRewardTransaction> },
  IPageVariables & { memberId: string }
> = gql`
  ${TRANSACTION_FIELDS}
  query GetRewardMemberTransactions($memberId: ID!, $pageNumber: Int!, $pageSize: Int!) {
    rewardMemberTransactions(memberId: $memberId, pageNumber: $pageNumber, pageSize: $pageSize) {
      items {
        ...RewardTransactionFields
        internalNotes
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_REWARD_MEMBER_REDEMPTIONS: TypedDocumentNode<
  { rewardMemberRedemptions: IConnection<IRedemption> },
  IPageVariables & { memberId: string }
> = gql`
  ${REDEMPTION_FIELDS}
  query GetRewardMemberRedemptions($memberId: ID!, $pageNumber: Int!, $pageSize: Int!) {
    rewardMemberRedemptions(memberId: $memberId, pageNumber: $pageNumber, pageSize: $pageSize) {
      items {
        ...RedemptionFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const ADJUST_REWARD_MEMBER_POINTS: TypedDocumentNode<
  { adjustRewardMemberPoints: IRewardTransaction },
  { input: IPointAdjustmentInput }
> = gql`
  ${TRANSACTION_FIELDS}
  mutation AdjustRewardMemberPoints($input: PointAdjustmentInput!) {
    adjustRewardMemberPoints(input: $input) {
      ...RewardTransactionFields
    }
  }
`;

/* ---------------------------- Redemptions ----------------------------- */

export interface IRedemptionAdminFilterInput {
  search?: string;
  statuses?: string[];
  fulfillmentStatuses?: string[];
  memberId?: string;
  catalogItemId?: string;
  from?: string;
  to?: string;
}

export const GET_REWARD_REDEMPTIONS: TypedDocumentNode<
  { rewardRedemptions: IConnection<IRedemption> },
  IPageVariables & { filter?: IRedemptionAdminFilterInput }
> = gql`
  ${REDEMPTION_FIELDS}
  query GetRewardRedemptions($pageNumber: Int!, $pageSize: Int!, $filter: RewardRedemptionFilterInput) {
    rewardRedemptions(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        ...RedemptionFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_REWARD_REDEMPTION: TypedDocumentNode<
  { rewardRedemption: IRedemption | null },
  { id: string }
> = gql`
  ${REDEMPTION_FIELDS}
  query GetRewardRedemption($id: ID!) {
    rewardRedemption(id: $id) {
      ...RedemptionFields
    }
  }
`;

export const FULFILL_REWARD_REDEMPTION: TypedDocumentNode<
  { fulfillRewardRedemption: IRedemption },
  { id: string; note?: string }
> = gql`
  ${REDEMPTION_FIELDS}
  mutation FulfillRewardRedemption($id: ID!, $note: String) {
    fulfillRewardRedemption(id: $id, note: $note) {
      ...RedemptionFields
    }
  }
`;

export const CANCEL_REWARD_REDEMPTION_ADMIN: TypedDocumentNode<
  { cancelRewardRedemption: IRedemption },
  { id: string; reason: string }
> = gql`
  ${REDEMPTION_FIELDS}
  mutation CancelRewardRedemptionAdmin($id: ID!, $reason: String!) {
    cancelRewardRedemption(id: $id, reason: $reason) {
      ...RedemptionFields
    }
  }
`;

export const REVERSE_REWARD_REDEMPTION: TypedDocumentNode<
  { reverseRewardRedemption: IRedemption },
  { id: string; reason: string }
> = gql`
  ${REDEMPTION_FIELDS}
  mutation ReverseRewardRedemption($id: ID!, $reason: String!) {
    reverseRewardRedemption(id: $id, reason: $reason) {
      ...RedemptionFields
    }
  }
`;

/* ------------------------------- Audit -------------------------------- */

export interface IAuditFilterInput {
  entityTypes?: string[];
  actions?: string[];
  actor?: string;
  entityId?: string;
  from?: string;
  to?: string;
}

export const GET_REWARD_AUDIT_LOG: TypedDocumentNode<
  { rewardAuditLog: IConnection<IRewardAuditEntry> },
  IPageVariables & { filter?: IAuditFilterInput }
> = gql`
  query GetRewardAuditLog($pageNumber: Int!, $pageSize: Int!, $filter: RewardAuditFilterInput) {
    rewardAuditLog(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        id
        actor
        action
        entityType
        entityId
        entityLabel
        previousValueSummary
        newValueSummary
        timestamp
        reason
        correlationId
      }
      ${PAGE_FIELDS}
    }
  }
`;

/* ------------------------- Entity search ------------------------------ */

/** Variables shared by every entity-search query used by the rule builder. */
export interface IEntitySearchVariables {
  query: string;
  limit?: number;
}

/** The entity kinds supported by the rule builder selectors. */
export type RewardEntityKind = "event" | "promoter" | "product" | "category" | "campaign";

/** Result of an entity-search query: `{ [field]: IEntityReference[] }`. */
export type EntitySearchResult<TField extends string> = Record<TField, IEntityReference[]>;

/**
 * Builds an entity-search query document. Every search shares the same
 * `(query: String!, limit: Int)` arguments and `{ id label sublabel }` selection,
 * so the five admin search documents are generated instead of copy-pasted.
 */
export function createEntitySearchDocument<TField extends string>(
  operationName: string,
  field: TField,
): TypedDocumentNode<EntitySearchResult<TField>, IEntitySearchVariables> {
  return gql(`
    query ${operationName}($query: String!, $limit: Int) {
      ${field}(query: $query, limit: $limit) {
        id
        label
        sublabel
      }
    }
  `);
}

export const SEARCH_REWARD_EVENTS = createEntitySearchDocument("SearchRewardEvents", "searchRewardEvents");
export const SEARCH_REWARD_PROMOTERS = createEntitySearchDocument("SearchRewardPromoters", "searchRewardPromoters");
export const SEARCH_REWARD_PRODUCTS = createEntitySearchDocument("SearchRewardProducts", "searchRewardProducts");
export const SEARCH_REWARD_CATEGORIES = createEntitySearchDocument("SearchRewardCategories", "searchRewardCategories");
export const SEARCH_REWARD_CAMPAIGNS = createEntitySearchDocument("SearchRewardCampaigns", "searchRewardCampaigns");

/** Entity-search document and result field per {@link RewardEntityKind}. */
export const REWARD_ENTITY_SEARCHES: Record<
  RewardEntityKind,
  {
    document: TypedDocumentNode<Partial<EntitySearchResult<string>>, IEntitySearchVariables>;
    field: string;
  }
> = {
  event: { document: SEARCH_REWARD_EVENTS, field: "searchRewardEvents" },
  promoter: { document: SEARCH_REWARD_PROMOTERS, field: "searchRewardPromoters" },
  product: { document: SEARCH_REWARD_PRODUCTS, field: "searchRewardProducts" },
  category: { document: SEARCH_REWARD_CATEGORIES, field: "searchRewardCategories" },
  campaign: { document: SEARCH_REWARD_CAMPAIGNS, field: "searchRewardCampaigns" },
};
